import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Package,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Filter,
  RefreshCw,
  ShoppingCart
} from 'lucide-react';
import { adPackageApi } from '../services/api';
import { AdPurchase, AdPurchaseStatusUpdate } from '../types';
import { formatCurrency } from '../utils/currency';
import { extractApiError } from '../utils/errorUtils';
import Decimal from 'decimal.js';

interface AnalyticsSummary {
  total_purchases: number;
  completed_purchases: number;
  pending_purchases: number;
  total_revenue_cents: number;  // Legacy field (sum of all currencies)
  revenue_by_currency: Record<string, number>;  // Issue #996: Revenue breakdown by currency
  conversion_rate: number;
}

export default function AdminAdPurchaseManagement() {
  const [purchases, setPurchases] = useState<AdPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPurchase, setSelectedPurchase] = useState<AdPurchase | null>(null);
  const [updatingPurchase, setUpdatingPurchase] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const pageSize = 50;

  // Analytics
  const [analytics, setAnalytics] = useState<AnalyticsSummary>({
    total_purchases: 0,
    completed_purchases: 0,
    pending_purchases: 0,
    total_revenue_cents: 0,
    revenue_by_currency: {},  // Issue #996
    conversion_rate: 0
  });

  // Memoize currency data to avoid repeated Object.keys/values/entries calls (Issue #996)
  const currencyData = useMemo(() => {
    const entries = Object.entries(analytics.revenue_by_currency);
    return {
      count: entries.length,
      entries: entries.sort(([, a], [, b]) => b - a), // Sort by revenue descending
      firstCurrency: entries.length > 0 ? entries[0][0] : null,
      firstAmount: entries.length > 0 ? entries[0][1] : null
    };
  }, [analytics.revenue_by_currency]);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoadingAnalytics(true);
      const analyticsData = await adPackageApi.adminGetAnalytics();

      setAnalytics({
        total_purchases: analyticsData.total_purchases,
        completed_purchases: analyticsData.completed_purchases,
        pending_purchases: analyticsData.pending_purchases,  // Fixed: Use API value instead of calculating (Issue #974)
        total_revenue_cents: analyticsData.total_revenue_cents,
        revenue_by_currency: analyticsData.revenue_by_currency || {},  // Issue #996
        conversion_rate: analyticsData.conversion_rate
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load analytics:', apiError);
      // Don't show error for analytics failure, just log it
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  const loadPurchases = useCallback(async () => {
    try {
      setLoading(true);
      const params: {
        page: number;
        page_size: number;
        payment_status?: 'pending' | 'completed' | 'failed' | 'refunded';
      } = {
        page: currentPage,
        page_size: pageSize
      };

      if (statusFilter) {
        params.payment_status = statusFilter as 'pending' | 'completed' | 'failed' | 'refunded';
      }

      const response = await adPackageApi.adminGetPurchases(params);
      setPurchases(response.items);
      setTotalPages(response.total_pages);
      setTotalPurchases(response.total);

      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load purchases:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter]);

  useEffect(() => {
    loadPurchases();
  }, [loadPurchases]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleUpdateStatus = async (
    purchaseId: number,
    newStatus: 'pending' | 'completed' | 'failed' | 'refunded',
    paymentMethod?: string,
    transactionId?: string
  ) => {
    // Confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to update purchase #${purchaseId} status to "${newStatus}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setUpdatingPurchase(purchaseId);
      setError(null);

      const statusUpdate: AdPurchaseStatusUpdate = {
        payment_status: newStatus,
        payment_method: paymentMethod,
        transaction_id: transactionId
      };

      const updatedPurchase = await adPackageApi.adminUpdatePurchaseStatus(
        purchaseId,
        statusUpdate
      );

      // Update local state
      setPurchases(prev =>
        prev.map(p => (p.id === purchaseId ? updatedPurchase : p))
      );

      // Refresh analytics to reflect status change
      loadAnalytics();

      setSuccessMessage(`Purchase #${purchaseId} status updated to ${newStatus}`);
      setTimeout(() => setSuccessMessage(null), 5000);

      // Close modal
      setSelectedPurchase(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to update purchase status:', apiError);
      setError(apiError.message);
    } finally {
      setUpdatingPurchase(null);
    }
  };

  const formatPrice = (cents: number, currency: string) => {
    const amountDecimal = new Decimal(cents).dividedBy(100);
    return formatCurrency(amountDecimal, currency);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'refunded':
        return <AlertCircle className="h-5 w-5 text-orange-600" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'failed':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'refunded':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Advertisement Purchase Management</h1>
        <p className="text-gray-600 mt-2">Manage all advertisement purchases and payment statuses</p>
      </div>

      {/* Analytics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="analytics-dashboard">
        {loadingAnalytics ? (
          // Loading skeleton
          <>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                    <div className="h-8 bg-gray-200 rounded w-16"></div>
                  </div>
                  <div className="h-8 w-8 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </>
        ) : (
          // Analytics cards
          <>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Purchases</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {analytics.total_purchases}
                  </p>
                </div>
                <ShoppingCart className="h-8 w-8 text-secondary" />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {analytics.completed_purchases}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600 mt-1">
                    {analytics.pending_purchases}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <DollarSign className="h-8 w-8 text-secondary" />
              </div>
              <div>
                {currencyData.count === 0 ? (
                  <p className="text-2xl font-bold text-gray-900">$0.00</p>
                ) : currencyData.count === 1 ? (
                  // Single currency - display prominently
                  <p className="text-2xl font-bold text-gray-900">
                    {formatPrice(currencyData.firstAmount!, currencyData.firstCurrency!)}
                  </p>
                ) : (
                  // Multiple currencies - list all (pre-sorted by revenue descending)
                  <div className="space-y-1">
                    {currencyData.entries.map(([currency, cents]) => (
                      <div key={currency} className="flex justify-between items-baseline">
                        <span className="text-xs text-gray-500 mr-2">{currency}</span>
                        <span className="text-lg font-semibold text-gray-900">
                          {formatPrice(cents, currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-start">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
          <button
            type="button"
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-800">{error}</p>
            <button
              type="button"
              onClick={loadPurchases}
              className="text-sm text-red-600 hover:text-red-800 underline mt-1"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              data-testid="admin-ad-purchase-status-filter"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={loadPurchases}
          className="flex items-center space-x-2 text-secondary hover:text-primary-dark"
          data-testid="admin-ad-purchase-refresh-button"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading purchases...</p>
        </div>
      )}

      {/* Purchases Table */}
      {!loading && purchases.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Purchase
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Package
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {purchases.map((purchase) => (
                  <tr key={purchase.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(purchase.payment_status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            #{purchase.id}
                          </div>
                          <div className="text-sm text-gray-500">
                            {purchase.advertisement_title || 'No title'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {purchase.package_name || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {purchase.vendor_name || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatPrice(purchase.amount_cents, purchase.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {purchase.purchase_date ? formatDate(purchase.purchase_date) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(purchase.payment_status)}>
                        {purchase.payment_status.charAt(0).toUpperCase() +
                          purchase.payment_status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        type="button"
                        onClick={() => setSelectedPurchase(purchase)}
                        className="text-secondary hover:text-primary-dark flex items-center space-x-1"
                        data-testid={`admin-ad-purchase-view-${purchase.id}`}
                      >
                        <Eye className="h-4 w-4" />
                        <span>View</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && purchases.length === 0 && (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No purchases found</p>
          {statusFilter && (
            <button
              type="button"
              onClick={() => setStatusFilter('')}
              className="text-secondary hover:text-primary-dark font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {purchases.length} of {totalPurchases} purchases
          </div>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Purchase Details Modal */}
      {selectedPurchase && (
        <PurchaseDetailsModal
          purchase={selectedPurchase}
          onClose={() => setSelectedPurchase(null)}
          onUpdateStatus={handleUpdateStatus}
          isUpdating={updatingPurchase === selectedPurchase.id}
        />
      )}
    </div>
  );
}

// Purchase Details Modal Component
interface PurchaseDetailsModalProps {
  purchase: AdPurchase;
  onClose: () => void;
  onUpdateStatus: (
    purchaseId: number,
    newStatus: 'pending' | 'completed' | 'failed' | 'refunded',
    paymentMethod?: string,
    transactionId?: string
  ) => Promise<void>;
  isUpdating: boolean;
}

function PurchaseDetailsModal({
  purchase,
  onClose,
  onUpdateStatus,
  isUpdating
}: PurchaseDetailsModalProps) {
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState(purchase.payment_status);
  const [paymentMethod, setPaymentMethod] = useState(purchase.payment_method || '');
  const [transactionId, setTransactionId] = useState(purchase.transaction_id || '');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Focus management: save previous focus and set focus on mount
  useEffect(() => {
    previousActiveElement.current = document.activeElement as HTMLElement;
    closeButtonRef.current?.focus();

    return () => {
      // Restore focus when modal closes
      previousActiveElement.current?.focus();
    };
  }, []);

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  const formatPrice = (cents: number, currency: string) => {
    const amountDecimal = new Decimal(cents).dividedBy(100);
    return formatCurrency(amountDecimal, currency);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'refunded':
        return 'text-orange-600';
      default:
        return 'text-yellow-600';
    }
  };

  const handleSubmitStatusUpdate = async () => {
    await onUpdateStatus(
      purchase.id,
      newStatus,
      paymentMethod || undefined,
      transactionId || undefined
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="purchase-details-title"
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 id="purchase-details-title" className="text-2xl font-bold text-gray-900">Purchase Details</h2>
              <p className="text-sm text-gray-600 mt-1">Purchase ID: #{purchase.id}</p>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close dialog"
              data-testid="admin-ad-purchase-modal-close"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Status */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Payment Status</h3>
              <div className="flex items-center justify-between">
                <p className={`text-lg font-semibold ${getStatusColor(purchase.payment_status)}`}>
                  {purchase.payment_status.charAt(0).toUpperCase() +
                    purchase.payment_status.slice(1)}
                </p>
                <button
                  type="button"
                  onClick={() => setShowStatusUpdate(!showStatusUpdate)}
                  className="text-sm text-secondary hover:text-primary-dark font-medium"
                  data-testid="admin-ad-purchase-update-status-button"
                >
                  {showStatusUpdate ? 'Cancel Update' : 'Update Status'}
                </button>
              </div>

              {/* Status Update Form */}
              {showStatusUpdate && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Status
                    </label>
                    <select
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value as typeof newStatus)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      data-testid="admin-ad-purchase-status-select"
                    >
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method (Optional)
                    </label>
                    <input
                      type="text"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      placeholder="e.g., Stripe, Manual Payment"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      data-testid="admin-ad-purchase-payment-method-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transaction ID (Optional)
                    </label>
                    <input
                      type="text"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      placeholder="e.g., txn_1234567890"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      data-testid="admin-ad-purchase-transaction-id-input"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmitStatusUpdate}
                    disabled={isUpdating}
                    className="w-full bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="admin-ad-purchase-submit-status-update"
                  >
                    {isUpdating ? 'Updating...' : 'Update Status'}
                  </button>
                </div>
              )}
            </div>

            {/* Package Details */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Package</h3>
              <p className="text-base font-medium text-gray-900">
                {purchase.package_name || 'Unknown Package'}
              </p>
            </div>

            {/* Advertisement Details */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Advertisement</h3>
              <p className="text-base font-medium text-gray-900">
                {purchase.advertisement_title || 'No title'}
              </p>
            </div>

            {/* User and Vendor Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">User ID</h3>
                <p className="text-base text-gray-900">{purchase.user_id}</p>
              </div>
              {purchase.vendor_name && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Vendor</h3>
                  <p className="text-base text-gray-900">{purchase.vendor_name}</p>
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Purchase Date
                </h3>
                <p className="text-base text-gray-900">
                  {purchase.purchase_date ? formatDate(purchase.purchase_date) : 'N/A'}
                </p>
              </div>

              {purchase.start_date && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    Start Date
                  </h3>
                  <p className="text-base text-gray-900">{formatDate(purchase.start_date)}</p>
                </div>
              )}

              {purchase.end_date && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    End Date
                  </h3>
                  <p className="text-base text-gray-900">{formatDate(purchase.end_date)}</p>
                </div>
              )}
            </div>

            {/* Payment Details */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                Payment Information
              </h3>
              <div className="bg-gray-50 rounded-md p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Amount:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatPrice(purchase.amount_cents, purchase.currency)}
                  </span>
                </div>
                {purchase.payment_method && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Payment Method:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {purchase.payment_method}
                    </span>
                  </div>
                )}
                {purchase.transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Transaction ID:</span>
                    <span className="text-sm font-mono text-xs text-gray-900">
                      {purchase.transaction_id}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
              {purchase.created_at && (
                <div>Created: {new Date(purchase.created_at).toLocaleString()}</div>
              )}
              {purchase.updated_at && (
                <div>Last Updated: {new Date(purchase.updated_at).toLocaleString()}</div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
